import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { loadStripe } from "@stripe/stripe-js";
import { Elements, PaymentElement, useStripe, useElements } from "@stripe/react-stripe-js";
import { api, fmtMoney, formatApiError } from "../lib/api";

// Cache the Stripe.js promise so loadStripe runs only once per page session.
let stripePromise = null;
async function getStripe() {
  if (!stripePromise) {
    const { data } = await api.get("/payments/config");
    if (!data?.publishable_key) throw new Error("Stripe publishable key not configured.");
    stripePromise = loadStripe(data.publishable_key);
  }
  return stripePromise;
}

function CheckoutInner({ intentId, amount, currency, onSuccess, onClose }) {
  const stripe = useStripe();
  const elements = useElements();
  const nav = useNavigate();
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;
    setBusy(true);
    setMsg("");
    const { error, paymentIntent } = await stripe.confirmPayment({
      elements,
      redirect: "if_required",
      confirmParams: {
        return_url: window.location.origin + "/portfolio",
      },
    });
    if (error) {
      setMsg(error.message || "Payment failed.");
      setBusy(false);
      return;
    }
    if (paymentIntent?.status === "succeeded") {
      try {
        const { data } = await api.post(`/payments/intent/${intentId}/credit`);
        if (data.credited) {
          toast.success(
            data.ucs_tons_added
              ? `Purchased ${Number(data.ucs_tons_added).toFixed(3)} tCO₂ from ${data.project_name || "project"}.`
              : "Payment confirmed."
          );
          onSuccess?.(data);
          onClose?.();
          nav("/portfolio");
          return;
        }
        setMsg(`Payment status: ${data.status}. Refresh portfolio in a moment.`);
      } catch (err) {
        setMsg(formatApiError(err?.response?.data?.detail) || "Payment was charged but we couldn't credit instantly. It will reconcile shortly.");
      }
    } else {
      setMsg(`Payment status: ${paymentIntent?.status || "unknown"}.`);
    }
    setBusy(false);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-5" data-testid="elements-form">
      <PaymentElement options={{ layout: "tabs" }} />
      {msg && (
        <div className="text-xs font-mono text-danger border border-danger/40 bg-danger/5 px-3 py-2" data-testid="elements-msg">
          {msg}
        </div>
      )}
      <div className="flex items-center justify-between gap-3 pt-2">
        <button
          type="button"
          onClick={onClose}
          disabled={busy}
          className="text-xs font-mono uppercase tracking-wider text-ink2 hover:text-ink underline underline-offset-4 disabled:opacity-40"
          data-testid="elements-cancel"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={!stripe || busy}
          className="btn-primary !px-6 !py-3 !text-xs disabled:opacity-50"
          data-testid="elements-pay-btn"
        >
          {busy ? "Processing…" : `Pay ${fmtMoney(amount, currency)} now`}
        </button>
      </div>
    </form>
  );
}

export default function StripeElementsDialog({ open, onClose, projectId, projectName, tons, currency, onSuccess }) {
  const [intent, setIntent] = useState(null);
  const [stripeJs, setStripeJs] = useState(null);
  const [loadErr, setLoadErr] = useState("");

  useEffect(() => {
    if (!open) {
      setIntent(null);
      setLoadErr("");
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const sp = await getStripe();
        if (cancelled) return;
        setStripeJs(sp);
        const { data } = await api.post("/payments/intent/ucs", {
          project_id: projectId,
          tons: Number(tons),
          currency,
          origin_url: window.location.origin,
        });
        if (cancelled) return;
        setIntent(data);
      } catch (e) {
        setLoadErr(formatApiError(e?.response?.data?.detail) || e.message || "Could not start checkout.");
      }
    })();
    return () => { cancelled = true; };
  }, [open, projectId, tons, currency]);

  const options = useMemo(() => {
    if (!intent) return null;
    return { clientSecret: intent.client_secret, appearance: { theme: "flat", variables: { colorPrimary: "#0a4220", fontFamily: "Inter, system-ui, sans-serif", borderRadius: "0px" } } };
  }, [intent]);

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[100] bg-ink/60 backdrop-blur-sm flex items-end sm:items-center justify-center p-4" onClick={onClose}>
      <div
        className="bg-surface border border-line w-full max-w-lg max-h-[90vh] overflow-y-auto shadow-xl"
        onClick={(e) => e.stopPropagation()}
        data-testid="stripe-elements-dialog"
      >
        <div className="border-b border-line px-6 py-4 flex items-center justify-between">
          <div>
            <div className="label-tag">In-app checkout · Stripe</div>
            <div className="font-serif text-xl mt-1">{projectName || "UCS purchase"}</div>
            <div className="text-xs font-mono text-ink2 mt-1">
              {Number(tons).toFixed(3)} tCO₂ · {currency}
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-ink2 hover:text-ink text-2xl leading-none"
            aria-label="Close"
            data-testid="elements-close"
          >×</button>
        </div>
        <div className="p-6">
          {loadErr && <div className="text-sm text-danger" data-testid="elements-load-err">{loadErr}</div>}
          {!loadErr && !intent && (
            <div className="text-sm text-ink2 font-mono" data-testid="elements-loading">Preparing secure card form…</div>
          )}
          {intent && options && stripeJs && (
            <Elements stripe={stripeJs} options={options}>
              <CheckoutInner
                intentId={intent.intent_id}
                amount={intent.amount}
                currency={intent.currency}
                onSuccess={onSuccess}
                onClose={onClose}
              />
            </Elements>
          )}
        </div>
        <div className="border-t border-line px-6 py-3 text-[10px] font-mono text-ink2 tracking-wider uppercase">
          🔒 Encrypted by Stripe · PCI DSS Level 1 · 3-D Secure where required
        </div>
      </div>
    </div>
  );
}
